import { useEffect, useState } from "react";
import { Coins, Loader2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { money } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";
import { creditLabel } from "@/components/AppShell";
import { fmtDate } from "@/pages/Dashboard";

const STATUS_STYLE = {
  paid: "border-emerald-500/40 text-emerald-300", initiated: "border-slate-600 text-slate-400",
  cancelled: "border-slate-600 text-slate-500", expired: "border-slate-600 text-slate-500", failed: "border-red-500/40 text-red-300",
};
const STATUS_LABEL = { initiated: "pending" };

export function StatusPill({ status, testId }) {
  return (
    <span data-testid={testId} className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLE[status] || STATUS_STYLE.initiated}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export const signed = (n) => (n > 0 ? `+${n}` : `${n}`);

export function Panel({ title, children, testId, right }) {
  return (
    <section data-testid={testId} className="rounded-2xl border border-slate-800 bg-[#F2F2F7]/60">
      <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
        <h2 className="text-base font-bold text-slate-100 md:text-lg">{title}</h2>{right}
      </div>
      {children}
    </section>
  );
}

export function PurchaseTable({ rows, prefix = "purchase" }) {
  if (!rows.length) return <p data-testid={`${prefix}-empty`} className="px-6 py-10 text-center text-sm text-slate-500">No purchases yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px]" data-testid={`${prefix}-table`}>
        <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <th className="px-6 py-3">Date</th><th className="px-6 py-3">Pack</th><th className="px-6 py-3 text-right">Credits</th>
          <th className="px-6 py-3 text-right">Amount</th><th className="px-6 py-3 text-right">Status</th></tr></thead>
        <tbody>{rows.map((p, i) => (
          <tr key={p.id} data-testid={`${prefix}-row-${i}`} className="border-t border-slate-800/70 text-sm">
            <td className="px-6 py-3 text-slate-400">{fmtDate(p.created_at)}</td>
            <td className="px-6 py-3 text-slate-100">{p.pack_name}</td>
            <td className="px-6 py-3 text-right font-mono-num text-slate-200">{p.credits}</td>
            <td className="px-6 py-3 text-right font-mono-num text-slate-200" data-testid={`${prefix}-amount-${i}`}>{money(p.amount)}</td>
            <td className="px-6 py-3 text-right"><StatusPill status={p.status} testId={`${prefix}-status-${i}`} /></td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export function LedgerTable({ rows, prefix = "credit-history" }) {
  if (!rows.length) return <p data-testid={`${prefix}-empty`} className="px-6 py-10 text-center text-sm text-slate-500">No credit activity yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px]" data-testid={`${prefix}-table`}>
        <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <th className="px-6 py-3">Date</th><th className="px-6 py-3 text-right">Change</th><th className="px-6 py-3">Reason</th>
          <th className="px-6 py-3 text-right">Balance after</th></tr></thead>
        <tbody>{rows.map((h, i) => (
          <tr key={h.id} data-testid={`${prefix}-row-${i}`} className="border-t border-slate-800/70 text-sm">
            <td className="px-6 py-3 text-slate-400">{new Date(h.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</td>
            <td data-testid={`${prefix}-change-${i}`} className={`px-6 py-3 text-right font-mono-num font-semibold ${h.change > 0 ? "text-emerald-300" : "text-red-300"}`}>{signed(h.change)}</td>
            <td data-testid={`${prefix}-label-${i}`} className="px-6 py-3 text-slate-200">{h.label}</td>
            <td className="px-6 py-3 text-right font-mono-num text-slate-400">{h.balance_after}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function PackCard({ pack, idx, onBuy, busy }) {
  return (
    <div data-testid={`pack-card-${idx}`} className="group flex flex-col rounded-2xl border border-slate-800 bg-[#F2F2F7]/70 p-6 transition-colors hover:border-amber-500/50">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{pack.name}</p>
      <p className="mt-4 font-display text-4xl font-extrabold text-slate-50"><span data-testid={`pack-credits-${idx}`}>{pack.credits}</span> <span className="text-base font-medium text-slate-400">credits</span></p>
      <p data-testid={`pack-price-${idx}`} className="mt-2 font-mono-num text-2xl font-semibold text-amber-300">{money(pack.price)}</p>
      <p data-testid={`pack-per-credit-${idx}`} className="mt-1 text-xs text-slate-500">{money(pack.price / pack.credits)} per credit</p>
      <button data-testid={`buy-pack-button-${idx}`} onClick={() => onBuy(pack)} disabled={!!busy}
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-60">
        {busy === pack.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />} Buy
      </button>
    </div>
  );
}

export default function Billing() {
  const { setCredits } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    api.get("/billing").then((r) => { setData(r.data); setCredits(r.data.balance); }).catch((e) => toast.error(errorMessage(e)));
  }, [setCredits]);
  const buy = async (pack) => {
    setBusy(pack.id);
    try {
      const { data: s } = await api.post("/billing/checkout", { pack_id: pack.id, origin_url: window.location.origin });
      window.location.href = s.url;
    } catch (e) {
      toast.error(errorMessage(e, "Could not start checkout"));
      setBusy(null);
    }
  };
  if (!data) return <div data-testid="billing-loading" className="h-64 animate-pulse rounded-2xl bg-slate-900/40" />;
  return (
    <div className="space-y-10" data-testid="billing-page">
      <div className="rise flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">Billing</h1>
          <p className="mt-3 max-w-xl text-sm text-slate-400 md:text-base">Each upload uses 1 credit for schedules, or the plans-only price when there is no schedule. Failed or empty reads are refunded. Editing, saving and PDFs are free.</p>
        </div>
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 px-6 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400/80">Balance</p>
          <p data-testid="billing-balance" className="mt-1 flex items-center gap-2 font-display text-3xl font-extrabold text-amber-200"><Coins className="h-6 w-6" />{creditLabel(data.balance)}</p>
        </div>
      </div>
      <div className="rise rise-1 grid gap-5 sm:grid-cols-2 xl:grid-cols-4" data-testid="pack-list">
        {data.packs.map((p, i) => <PackCard key={p.id} pack={p} idx={i} onBuy={buy} busy={busy} />)}
        {!data.packs.length && <p data-testid="packs-empty" className="text-sm text-slate-500">No credit packs are available right now.</p>}
      </div>
      <p className="-mt-4 text-xs text-slate-500">Prices in USD. Secure payment by Stripe.</p>
      <div className="rise rise-2 grid gap-8 xl:grid-cols-2">
        <Panel title="Purchase history" testId="purchase-history"><PurchaseTable rows={data.purchases} /></Panel>
        <Panel title="Credit history" testId="credit-history"><LedgerTable rows={data.history} /></Panel>
      </div>
    </div>
  );
}
